import React, { useCallback } from 'react';
import {
  View,
  Text,
  ScrollView,
  StyleSheet,
  TouchableOpacity,
  StatusBar,
} from 'react-native';
import Icon from 'react-native-vector-icons/Ionicons';
import { RouteProp, useRoute, useNavigation } from '@react-navigation/native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { RootStackParamList } from '@/navigation/RootNavigator';
import { ICommunityResourceFile } from '@/types/residents/communityResource';
import { openExternalUrl } from '@/utils/common/linking';
import { showAlert } from '@/utils/common/alert';
import { PRIMITIVE_COLORS } from '@/constants/residents/residentColors';
import { useThemeColors } from '@/hooks/residents/useThemeColors';
import { REDESIGN_TYPOGRAPHY, REDESIGN_SPACING } from '@/constants/residents/homeConstants';

import { BaseResidentMobileScreen } from '@/components/residents/mobile/BaseResidentMobileScreen';
import { BackLink } from '@/components/residents/mobile/BackLink';
import { GradientBackground, GlassCard } from '@/components/residents/redesign';
import { ResourceIcon } from '@/components/residents/resources/ResourceIcon';
import { ImageViewerModal } from '@/components/common/ImageViewerModal';
import { FileRow } from '@/components/residents/resources/FileRow';

type ResourceDetailRouteProp = RouteProp<RootStackParamList, 'MobileResourceDetail'>;

const HP = REDESIGN_SPACING.horizontalPadding;

export const MobileResourceDetailScreen: React.FC = () => {
  const { colors } = useThemeColors();
  const route = useRoute<ResourceDetailRouteProp>();
  const navigation = useNavigation();
  const insets = useSafeAreaInsets();
  const { resource } = route.params;

  const title = resource.name || resource.title || '';
  const description = resource.description || '';

  const handleBack = useCallback(() => {
    navigation.goBack();
  }, [navigation]);

  const handleUrlPress = useCallback(async (url: string) => {
    const ok = await openExternalUrl(url);
    if (!ok) {
      showAlert('Error', 'Cannot open this URL');
    }
  }, []);

  const [imageViewer, setImageViewer] = React.useState<{ visible: boolean; uri: string | null }>({
    visible: false,
    uri: null,
  });

  const handleOpenImage = useCallback((file: ICommunityResourceFile) => {
    setImageViewer({ visible: true, uri: file.fileUrl });
  }, []);

  const handleCloseImage = useCallback(() => {
    setImageViewer({ visible: false, uri: null });
  }, []);

  const handleOpenPdf = useCallback((file: ICommunityResourceFile) => {
    (navigation as any).navigate('PdfScreen', {
      url: file.fileUrl,
      title: file.label || `File ${file.id}`,
    });
  }, [navigation]);

  return (
    <GradientBackground>
      <StatusBar barStyle="light-content" />

      <BaseResidentMobileScreen
        screenName="Resources"
        darkBackground={true}
        headerVariant="gradient"
      >
        <ScrollView
          style={styles.scrollView}
          contentContainerStyle={[
            styles.scrollContent,
            { paddingBottom: 40 + insets.bottom },
          ]}
          showsVerticalScrollIndicator={false}
        >
          <BackLink
            label="Back to all resources"
            onPress={handleBack}
            color={PRIMITIVE_COLORS.resioBlue}
          />

          <GlassCard
            borderRadius={REDESIGN_SPACING.quickActionRadius}
            padding={REDESIGN_SPACING.cardPadding}
            style={styles.headerCard}
          >
            <View style={styles.headerRow}>
              <View style={[styles.iconCircle, { backgroundColor: colors.accent.blueBgLight }]}>
                <ResourceIcon
                  iconKey={resource.icon || resource.iconName}
                  size={24}
                  color={colors.text.primary}
                />
              </View>
              <View style={styles.headerText}>
                <Text style={[styles.title, { color: colors.text.primary }]}>{title}</Text>
                {description ? (
                  <Text style={[styles.description, { color: colors.text.muted }]}>{description}</Text>
                ) : null}
              </View>
            </View>
          </GlassCard>

          {resource.content && (
            <View style={styles.section}>
              <Text style={[styles.sectionLabel, { color: colors.text.subtle }]}>INFORMATION</Text>
              <GlassCard borderRadius={12} padding={16}>
                <Text style={[styles.sectionText, { color: colors.text.secondary }]}>{resource.content}</Text>
              </GlassCard>
            </View>
          )}

          {resource.url && (
            <View style={styles.section}>
              <Text style={[styles.sectionLabel, { color: colors.text.subtle }]}>WEBSITE</Text>
              <TouchableOpacity onPress={() => handleUrlPress(resource.url!)}>
                <GlassCard borderRadius={12} padding={14} style={styles.linkCard}>
                  <Icon name="link-outline" size={18} color={colors.text.primary} />
                  <Text style={styles.linkText} numberOfLines={1}>{resource.url}</Text>
                </GlassCard>
              </TouchableOpacity>
            </View>
          )}

          {resource.files && resource.files.length > 0 && (
            <View style={styles.section}>
              <Text style={[styles.sectionLabel, { color: colors.text.subtle }]}>
                {resource.files.length === 1 ? 'FILE' : 'FILES'}
              </Text>
              {resource.files.map((file) => (
                <FileRow
                  key={file.id}
                  file={file}
                  onOpenImage={handleOpenImage}
                  onOpenPdf={handleOpenPdf}
                />
              ))}
            </View>
          )}
        </ScrollView>
        <ImageViewerModal
          visible={imageViewer.visible}
          images={imageViewer.uri ? [imageViewer.uri] : []}
          onClose={handleCloseImage}
        />
      </BaseResidentMobileScreen>
    </GradientBackground>
  );
};

const styles = StyleSheet.create({
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingHorizontal: HP,
    gap: 20,
    paddingTop: 8,
  },
  headerCard: {
    gap: 0,
  },
  headerRow: {
    flexDirection: 'row',
    gap: 16,
    alignItems: 'flex-start',
  },
  iconCircle: {
    width: 52,
    height: 52,
    borderRadius: 16,
    justifyContent: 'center',
    alignItems: 'center',
  },
  headerText: {
    flex: 1,
    gap: 4,
  },
  title: {
    ...REDESIGN_TYPOGRAPHY.cardTitleLarge,
    fontSize: 18,
  },
  description: {
    ...REDESIGN_TYPOGRAPHY.body,
  },
  section: {
    gap: 8,
  },
  sectionLabel: {
    ...REDESIGN_TYPOGRAPHY.label,
  },
  sectionText: {
    ...REDESIGN_TYPOGRAPHY.body,
    lineHeight: 20,
  },
  linkCard: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  linkText: {
    ...REDESIGN_TYPOGRAPHY.body,
    color: PRIMITIVE_COLORS.resioBlue,
    flexShrink: 1,
  },
});
